import { SkyCard, SkyDataGrid, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "wouter";
import type {
  ConnectorAdminView,
  ConnectorUsageRow,
  ConnectorUsageView,
} from "@shared/types/connectorsAdmin";
import { ErrorState, GridSkeleton } from "@/components/platform/ScreenState";
import { CONNECTORS_ADMIN_PATH } from "@/hooks/use-connectors-admin";
import { useDisplayTz } from "@/hooks/use-display-tz";
import type { RunStatus } from "@shared/types/runs";
import type { ApiClientError } from "@/lib/api";
import { LIST_GRID_PAGINATION } from "@/lib/grid";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { absolutePath } from "@/lib/routes";
import {
  calendarDayInZone,
  formatInstant,
  instantHoverTitle,
  zoneCaption,
} from "@/lib/time/displayTime";
import {
  ConnectionStatusBadge,
  connectionStatusOf,
  connectionWaitingOf,
  listedRunOwnStatus,
  SettledImportLine,
  settledImportOf,
  type ConnectionStatus,
  type ConnectionStatusInput,
} from "@/components/connections/ConnectionStatus";

const CONNECTION_STATES: ReadonlySet<string> = new Set(["draft", "ready", "disabled"]);

/**
 * #993: the connection's status as the Connections list names it (`connectionStatusOf`), from the
 * same facts the list reads. A state this build does not know reads as a draft: never Active.
 */
export function usageRowStatus(row: ConnectorUsageRow): ConnectionStatus {
  const state = (
    CONNECTION_STATES.has(row.state) ? row.state : "draft"
  ) as ConnectionStatusInput["state"];
  return connectionStatusOf({ ...row, state });
}

/**
 * #993 review: the Last run word, as the Connections list's Last import cell says it
 * (`LastRunOutcome`): this connection's own outcome in that run, the run's status only when the
 * server sends no `lastRun`, and "Waiting" when a readiness gate held it back. So a run that
 * covered several connections never reads "Partly failed" beside this one's "Failed" or "Active".
 */
export function usageRowLastRunLabel(row: ConnectorUsageRow): string | null {
  // #460 (option 2): the word names the run whose time is beside it, so a cancelled last run whose
  // outcome the server read from an earlier settled run reads Cancelled (the Status column keeps
  // the settled outcome).
  const shown = row.lastRun ? listedRunOwnStatus(row.lastRun) : row.lastRunStatus;
  if (!shown) return null;
  if (!settledImportOf(row.lastRun) && connectionWaitingOf(row)) return "Waiting";
  return runStatusPresentation(shown as RunStatus).label;
}

/**
 * When the last run finished, in the display zone: "Oct 14, 01:32 PM" within the current year,
 * and the day with its year, "Dec 14, 2025", for an older one, so it never reads as this year.
 * Either fits the column on one line; the exact time is the hover (`instantHoverTitle`).
 */
export function usageRowWhen(iso: string, zone: string, now: Date = new Date()): string {
  const then = calendarDayInZone(iso, zone);
  const today = calendarDayInZone(now, zone);
  const sameYear = then !== null && today !== null && then.year === today.year;
  return formatInstant(iso, zone, sameYear ? "short" : "day");
}

/**
 * A3 / B-27: where a connector is used, across every instance. After a refused Deactivate the
 * same table sits under "Delete these connections first": nothing is deactivated while a
 * connection uses it, and an instance that could not be read counts as unknown, not as unused.
 */
export function ConnectorUsageCard({
  connector,
  refusal,
}: {
  connector: ConnectorAdminView;
  refusal: ApiClientError | null;
}) {
  return (
    <SkyCard appearance="outline" testId="connector-usage-card">
      <div className="flex flex-col gap-3 p-1">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block>
            {refusal ? "Delete these connections first" : `Used by ${connector.displayName}`}
          </SkyText>
          <SkyText size={200} color="muted" block>
            {refusal
              ? refusal.message
              : "Every connection that uses this connector, in every instance."}
          </SkyText>
        </div>
        <ConnectorUsageBody name={connector.name} />
      </div>
    </SkyCard>
  );
}

/** The usage read behind the table, shared by the drawer's Deactivate (#1086). */
export function useConnectorUsage(name: string, enabled = true) {
  return useQuery<ConnectorUsageView>({
    queryKey: [`${CONNECTORS_ADMIN_PATH}/${name}/usage`],
    retry: false,
    enabled,
  });
}

/**
 * #1086: why Deactivate is off, or null when it may be pressed. The server refuses the same two
 * cases (`CONNECTOR_IN_USE`, `CONNECTOR_USAGE_UNKNOWN`); a usage read that failed leaves the
 * button on, so the server answers.
 */
export function deactivateBlockedReason(
  displayName: string,
  usage: Pick<ConnectorUsageView, "connections" | "unreadable"> | undefined,
): string | null {
  if (!usage) return null;
  // Both are named when both hold: the server refuses an unreadable instance first, so deleting
  // the connections alone would leave Deactivate off for a reason the bar never gave.
  const sentences: string[] = [];
  if (usage.connections > 0) {
    const noun = usage.connections === 1 ? "connection" : "connections";
    const them = usage.connections === 1 ? "it" : "them";
    sentences.push(`${displayName} is used by ${usage.connections} ${noun}. Delete ${them} first.`);
  }
  if (usage.unreadable.length > 0) {
    sentences.push(
      sentences.length > 0
        ? "Some instances could not be read either, so it cannot be deactivated until they can."
        : `Some instances could not be read, so ${displayName} cannot be deactivated yet.`,
    );
  }
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/**
 * The usage table itself, with its loading, error, empty, unreadable and unavailable states. Read
 * from `/platform/connectors/:name/usage`, which only a holder of `platform.connectors.manage`
 * can reach (this screen is PlatformAdmin's). The connector drawer shows it too (W2-11).
 */
export function ConnectorUsageBody({ name }: { name: string }) {
  const zone = useDisplayTz();
  const usage = useConnectorUsage(name);
  const columns: SkyDataGridColumn<ConnectorUsageRow>[] = [
    {
      key: "connectionName",
      header: "Connection",
      width: "19%",
      renderCell: (_value, row) => (
        <Link href={absolutePath(row.path)} className="font-medium underline underline-offset-2">
          {row.connectionName}
        </Link>
      ),
    },
    {
      key: "instanceName",
      header: "Instance",
      width: "17%",
      renderCell: (_value, row) => (
        <div className="flex flex-col">
          <SkyText size={300} block>
            {row.instanceName}
          </SkyText>
          <SkyText size={200} color="muted" block>
            {row.tenantName}
          </SkyText>
        </div>
      ),
    },
    { key: "image", header: "Image", width: "15%" },
    {
      key: "state",
      header: "State",
      // #993: "Setup incomplete", the widest status word, fits the drawer's grid (552 px) whole.
      width: "25%",
      renderCell: (_value, row) => (
        // Sized to its word, as on the Connections list, not stretched across the cell.
        <div className="flex items-start">
          <ConnectionStatusBadge
            status={usageRowStatus(row)}
            testId={`connector-usage-status-${row.connectionId}`}
          />
        </div>
      ),
    },
    {
      key: "lastRunStatus",
      header: "Last run",
      width: "24%",
      renderCell: (_value, row) => {
        const label = usageRowLastRunLabel(row);
        if (!label) return <SkyText size={200}>No run yet</SkyText>;
        const when = row.lastRunAt ? usageRowWhen(row.lastRunAt, zone) : null;
        // The status word, then when it finished, the time on one line (#993). #460: under a
        // cancelled last run that settled nothing, a third line, "Settled: Failed" on one line,
        // names and links the run the State column reads.
        return (
          <div className="flex min-w-0 flex-col">
            <SkyText size={200} block testId={`connector-usage-last-run-${row.connectionId}`}>
              {label}
            </SkyText>
            {when ? (
              <SkyText
                size={200}
                color="muted"
                block
                className="whitespace-nowrap"
                title={instantHoverTitle(row.lastRunAt, zone)}
                testId={`connector-usage-when-${row.connectionId}`}
              >
                {when}
              </SkyText>
            ) : null}
            {/* #460: the run the State column reads, when the last run settled nothing, linked
                into the row's instance like the Connection column (`row.path`). */}
            <SettledImportLine
              run={row.lastRun}
              instanceId={row.instanceId}
              compact
              testId={`connector-usage-settled-${row.connectionId}`}
            />
          </div>
        );
      },
    },
  ];

  function renderUsage(): ReactNode {
    if (usage.isPending) return <GridSkeleton rows={3} label="Loading usage…" />;
    if (usage.isError) {
      return (
        <ErrorState
          title="The usage list did not load"
          error={usage.error}
          onRetry={() => void usage.refetch()}
        />
      );
    }
    return (
      <>
        {usage.data.unreadable.length > 0 ? (
          <SkyText size={200} block testId="connector-usage-unreadable">
            {`${usage.data.unreadable.map((instance) => instance.instanceName).join(", ")} could not be read, so this list may be incomplete and the connector cannot be deactivated yet.`}
          </SkyText>
        ) : null}
        {usage.data.unavailable.length > 0 ? (
          <SkyText size={200} color="muted" block testId="connector-usage-unavailable">
            {`${usage.data.unavailable.map((instance) => instance.instanceName).join(", ")}: Instance unavailable (database removed).`}
          </SkyText>
        ) : null}
        {usage.data.rows.length === 0 ? (
          <SkyText size={200} color="muted" block>
            No connection uses this connector.
          </SkyText>
        ) : (
          <>
            <SkyDataGrid<ConnectorUsageRow>
              data={usage.data.rows}
              columns={columns}
              getRowId={(row) => `${row.instanceId}:${row.connectionId}`}
              {...LIST_GRID_PAGINATION}
              testId="connector-usage-grid"
            />
            <SkyText size={200} color="muted" block testId="connector-usage-zone">
              {`${zoneCaption(zone)}.`}
            </SkyText>
          </>
        )}
      </>
    );
  }

  return <>{renderUsage()}</>;
}
